import { Fragment, useCallback, useMemo } from "react";
import { Button, Image } from "@storybook";

import {
  Card,
  CombinedInput,
  Description,
  Divider,
  FormContainer,
  LabelDescription,
  SocialLoginOptions,
  Tabs,
} from "components";
import { useInstance, useTranslation } from "hooks";
import { SOCIAL_LOGIN_OPTIONS } from "components/social-login-options/constants";
import { ISignInCredType, useSignin } from "views/signin/stores";
import GlobalInstanceManager from "instance-manger";
import { FormWrapper } from "components/form-wrapper/form-wrapper";

/**
 * SigninForm Component
 */
export const SigninForm = () => {
  // Hook to get translation functions
  const { t } = useTranslation();
  const { webcomponentProps } = GlobalInstanceManager;

  // Hook to get instance details
  const { instanceDetails } = useInstance();
  // Hook to get signin state and handlers
  const {
    handleChange,
    handleChangeCountry,
    handleOnChangeMethod,
    moveToSignupPage,
    form: formState,
    currentAuthMethod,
    onSubmitCred,
    handleSocialSignUp,
    submitCredLoading,
    handleCombinedInputChange,
    loading,
  } = useSignin();

  const { logo, signUp, providers, properties } = useMemo(
    () => instanceDetails,
    [instanceDetails]
  );

  /**
   * Handles form submission
   */
  const handleSubmitCred = useCallback(async () => {
    await onSubmitCred();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSubmitCred]);

  /**
   * Determines if the submit button should be disabled
   */
  const isDisabled = useMemo(() => {
    const { value, isError } =
      (formState?.find((el) => el.name === currentAuthMethod) as any) ?? {};
    if (!value || isError) {
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState, currentAuthMethod]);

  const { fields } = useMemo(() => {
    return { fields: formState };
  }, [formState]);

  const ssoElemet = (
    <SocialLoginOptions
      options={SOCIAL_LOGIN_OPTIONS.filter(
        (el) =>
          providers?.map((elm) => elm.key)?.includes(el.key) ||
          (el.key === "simplici" && properties)
      )}
      isShowShowMoreSSO={false}
      handleSelect={handleSocialSignUp}
    />
  );

  const haveAccount = instanceDetails?.signUp?.required &&
    !webcomponentProps.hide_navigation && (
      <Description
        content={
          <div onClick={moveToSignupPage}>
            {t("signin.dont_have_account")} <span>{t("signin.register")}</span>
            {/* Clickable text to navigate to sign-in */}
          </div>
        }
        className="text-center signin-footer" // Center aligned text
      />
    );

  if (signUp?.scope.length === 0 && providers?.length) {
    return (
      <Card
        className="flex direction-col gap-16 border-0"
        isLoading={submitCredLoading || loading}
      >
        <>
          {ssoElemet}
          {haveAccount}
        </>
      </Card>
    );
  }

  const { iscombinedinput } = webcomponentProps;

  return (
    <Card
      className="flex direction-col gap-24 sm-border-0"
      isLoading={submitCredLoading || loading}
    >
      <Fragment>
        {logo && (
          <Image
            className="object-fit-contain h-48 w-fit-content w-170"
            url={logo}
          />
        )}
        {/* Label and description for the signin form */}
        <LabelDescription
          label={t("signin.label")}
          description={t("signin.sublabel")}
        />

        {/* Tabs for authentication methods if there are multiple sign-up scopes */}
        {!!signUp?.scope.length &&
          signUp?.scope.length > 1 &&
          !iscombinedinput && (
            <Tabs
              handleChange={(val) =>
                handleOnChangeMethod(val as ISignInCredType)
              }
              value={currentAuthMethod}
              options={t("signin.tabs")}
            />
          )}

        <FormWrapper>
          {/* Form container with input fields */}
          {iscombinedinput ? (
            <CombinedInput handleChange={handleCombinedInputChange} />
          ) : (
            <FormContainer
              form={fields}
              handleChange={handleChange}
              handleChangeCountry={handleChangeCountry}
            />
          )}

          {/* Submit button with loading state */}
          <Button
            label={t("signin.continue")}
            type="button__filled button__filled--primary  button__block"
            handleClick={handleSubmitCred}
            loader={submitCredLoading}
            disabled={submitCredLoading || isDisabled}
          />
        </FormWrapper>

        {/* Divider with "or" label */}
        <Divider label={t("or")} />

        {/* Social login options based on available providers */}
        {ssoElemet}

        {/* Checkbox for terms acceptance */}
        <Description className="text-center" content={t("signin.footer")} />

        {/* Link to sign-up page if required */}
        {!iscombinedinput && haveAccount}
      </Fragment>
    </Card>
  );
};
